import { useQuery } from '@tanstack/react-query';
import { supabase, GalleryAllImage } from '@/lib/supabase';
import { BGVGallery, GalleryAllImages, ReviewStatus, FranchiseRegion } from '@/types/gallery';

// Unified function to fetch all images (both direct photos and job images)
const fetchGalleryAllImages = async (galleryId: number): Promise<GalleryAllImage[]> => {
  try {
    // First, try to query the unified view if it exists
    const { data: viewData, error: viewError } = await supabase
      .from('gallery_all_images')
      .select('*')
      .eq('gallery_id', galleryId)
      .order('created_at', { ascending: false });
    
    if (!viewError && viewData) {
      return viewData;
    }
    
    // Fallback: If unified view doesn't exist, query gallery_photos only
    console.log('Unified view not available, falling back to gallery_photos only');
    const { data: photosData, error: photosError } = await supabase
      .from('gallery_photos')
      .select('*')
      .eq('gallery_id', galleryId)
      .order('sort_order', { ascending: true });
    
    if (photosError) {
      console.warn('gallery_photos table also unavailable:', photosError);
      return [];
    }
    
    // Transform gallery_photos to match GalleryAllImage interface
    const transformedPhotos: GalleryAllImage[] = (photosData || []).map(photo => ({
      image_type: 'gallery_photo' as const,
      original_id: photo.id,
      gallery_id: photo.gallery_id,
      image_url: photo.image_url,
      caption: photo.caption,
      review_status: 'approved' as const, // Direct uploads are always approved
      created_at: photo.created_at,
      sort_order: photo.sort_order
    }));
    
    return transformedPhotos;
  } catch (error) {
    console.error('Error fetching gallery images:', error);
    return [];
  }
};

// BGV API functions
const fetchBGVGalleries = async (options: {
  searchTerm?: string;
  reviewStatus?: ReviewStatus;
  franchiseRegion?: FranchiseRegion;
}): Promise<BGVGallery[]> => {
  try {
    let query = supabase
      .from('galleries')
      .select('*')
      .order('created_on', { ascending: false });

    // Apply filters
    if (options.searchTerm) {
      query = query.or(`title.ilike.%${options.searchTerm}%,description.ilike.%${options.searchTerm}%`);
    }

    if (options.reviewStatus) {
      query = query.eq('review_status', options.reviewStatus);
    }

    if (options.franchiseRegion) {
      query = query.eq('franchise_region', options.franchiseRegion);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching BGV galleries:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchBGVGalleries:', error);
    throw error;
  }
};

const fetchBGVGalleryImages = async (galleryId: number): Promise<GalleryAllImages[]> => {
  try {
    const { data, error } = await supabase
      .from('gallery_all_images')
      .select('*')
      .eq('gallery_id', galleryId)
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error fetching BGV gallery images:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchBGVGalleryImages:', error);
    throw error;
  }
};

// Track image usage for social media
const trackImageUsage = async (imageIds: number[], galleryId: number) => {
  try {
    // Update the used_for_social_media flag for the gallery
    const { error: galleryError } = await supabase
      .from('galleries')
      .update({ used_for_social_media: true })
      .eq('id', galleryId);

    if (galleryError) {
      console.error('Error updating gallery social media usage:', galleryError);
    }

    // Log the usage for audit purposes
    console.log(`Tracked social media usage for gallery ${galleryId}, images:`, imageIds);
  } catch (error) {
    console.error('Error tracking image usage:', error);
  }
};

interface UseGalleryImagesOptions {
  enabled?: boolean;
}

interface UseBGVGalleriesOptions {
  searchTerm?: string;
  reviewStatus?: ReviewStatus;
  franchiseRegion?: FranchiseRegion;
}

interface UseBGVGalleryImagesOptions {
  galleryId?: number;
  enabled?: boolean;
}

export const useGalleryImages = (galleryId: number | null, options: UseGalleryImagesOptions = {}) => {
  return useQuery({
    queryKey: ['gallery-images', galleryId],
    queryFn: () => galleryId ? fetchGalleryAllImages(galleryId) : Promise.resolve([]),
    enabled: !!galleryId && (options.enabled !== false),
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchOnWindowFocus: false
  });
};

// BGV-specific hooks
export const useBGVGalleries = (options: UseBGVGalleriesOptions = {}) => {
  return useQuery({
    queryKey: ['bgv-galleries', options],
    queryFn: () => fetchBGVGalleries(options),
    staleTime: 60000, // Consider data stale after 1 minute
    refetchOnWindowFocus: false
  });
};

export const useBGVGalleryImages = (options: UseBGVGalleryImagesOptions = {}) => {
  return useQuery({
    queryKey: ['bgv-gallery-images', options.galleryId],
    queryFn: () => options.galleryId ? fetchBGVGalleryImages(options.galleryId) : Promise.resolve([]),
    enabled: !!options.galleryId && (options.enabled !== false),
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchOnWindowFocus: false
  });
};

export { fetchGalleryAllImages, fetchBGVGalleries, fetchBGVGalleryImages, trackImageUsage };